import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { classnames, PlainButton, Tag, Tooltip } from "@culturehq/components";

import { useIsMobile } from "app/hooks";
import { t, Trans } from "app/utils";

import UserThumbnail from "components/person/UserThumbnail";
import PostStory from "components/stories/post-stories-v2/PostStory";
import PromptIcon from "components/stories/PromptIcon";
import PromptIconButton from "components/stories/PromptIconButton";
import OldAndChildStoryQuestionQuickOptions from "components/admin/settings/OldAndChildStoryQuestionQuickOptions";

export const MAX_USERS_TO_SHOW = 5;

const StartTheTrendButton = () => (
  <span className="ChildPrompts__Container-Users--Empty" role="img" aria-label="thunder">
    <svg enableBackground="new 0 0 512.002 512.002" height="30px" viewBox="0 0 512.002 512.002" width="30px">
      <g>
        <path d="m201.498 512.002c-1.992 0-4.008-.398-5.934-1.229-6.741-2.907-10.387-10.266-8.617-17.39l50.724-204.178h-136.67c-4.947 0-9.576-2.439-12.373-6.52s-3.402-9.278-1.617-13.892l100.262-259.204c2.235-5.779 7.793-9.589 13.989-9.589h137.961c5.069 0 9.795 2.56 12.565 6.806 2.768 4.246 3.206 9.603 1.162 14.242l-59.369 134.76h117.42c5.486 0 10.534 2.995 13.164 7.81 2.63 4.814 2.422 10.68-.543 15.296l-209.496 326.192c-2.833 4.412-7.651 6.896-12.628 6.896z" fill="#ffe645" data-original="#FFE645" style={{ fill: "#FFD24B" }} data-old_color="#ffe645" />
        <path d="m411.001 155.807h-117.42l59.369-134.76c2.044-4.639 1.606-9.996-1.162-14.242-2.769-4.245-7.495-6.805-12.564-6.805h-73.237v424.357l157.636-245.444c2.965-4.616 3.173-10.482.543-15.296-2.631-4.815-7.678-7.81-13.165-7.81z" fill="#ffcf2c" data-original="#FFCF2C" className="active-path" style={{ fill: "#E2BA42" }} data-old_color="#ffcf2c" />
      </g>
    </svg>
    <Trans>Start the trend</Trans>
  </span>
);

const PeopleWhoAnswered = ({ childStoryQuestion }) => (
  <>
    <p id="people-who-answer" className="hidden">The next users already answered this prompt</p>
    <div className="ChildPrompts__Container-Users" aria-describedby="people-who-answer">
      {childStoryQuestion.peopleWhoAnswered?.map((user, index) => (
        index < MAX_USERS_TO_SHOW && <UserThumbnail key={user.id} user={user} />
      ))}
      {childStoryQuestion.peopleWhoAnswered?.length > MAX_USERS_TO_SHOW && <span className="ApproveProfileModal__SelectedUsers__more chq-tmb">+{childStoryQuestion.peopleWhoAnswered.length - MAX_USERS_TO_SHOW}</span>}
    </div>
  </>
);

const AnswerPromptContent = ({ childStoryQuestion }) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <>
        <div className="ChildPrompts__Container-Left">
          <div div className="ChildPrompts-Mobile__Container">
            <p className="ChildPrompts__Container-Title">{childStoryQuestion.question}</p>
            {childStoryQuestion?.status === "paused" && (
              <Tag className="ChildPrompts__Tag" color="red"><Trans>Paused</Trans></Tag>
            )}
            {childStoryQuestion?.status === "hidden" && (
              <Tag className="ChildPrompts__Tag" color="red"><Trans>Hidden</Trans></Tag>
            )}
            {childStoryQuestion?.status === "archived" && (
              <Tag className="ChildPrompts__Tag" color="red"><Trans>Archived</Trans></Tag>
            )}
            <div className="ChildPrompts-Mobile__Footer">
              <div className="ChildPrompts__Container-Icon">
                <PromptIconButton allowedTypes={childStoryQuestion.allowedTypes} />
              </div>
              <PeopleWhoAnswered childStoryQuestion={childStoryQuestion} />
              {childStoryQuestion.peopleWhoAnswered?.length === 0 && (
                <StartTheTrendButton />
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="ChildPrompts__Container-Left">
        <div className="ChildPrompts__Container-Icon">
          <PromptIcon allowedTypes={childStoryQuestion.allowedTypes} />
        </div>
        <p className="ChildPrompts__Container-Title">{childStoryQuestion.question}</p>
        {childStoryQuestion?.status === "paused" && (
          <Tag className="ChildPrompts__Tag" color="red"><Trans>Paused</Trans></Tag>
        )}
        {childStoryQuestion?.status === "hidden" && (
          <Tag className="ChildPrompts__Tag" color="red"><Trans>Hidden</Trans></Tag>
        )}
        {childStoryQuestion?.status === "archived" && (
          <Tag className="ChildPrompts__Tag" color="red"><Trans>Archived</Trans></Tag>
        )}
      </div>
      <PeopleWhoAnswered childStoryQuestion={childStoryQuestion} />
      {childStoryQuestion.peopleWhoAnswered?.length === 0 && (
        <StartTheTrendButton />
      )}
    </>
  );
};

const ChildStoryQuestion = ({
  index,
  isParentTrendPausedStatus,
  isParentTrendArchivedStatus,
  childStoryQuestion,
  noOptions,
  onCreate,
  onUpdate,
  onReplace,
  onDelete,
  onAccept,
  handleSelectedPrompt
}) => {
  const isChildPausedStatus = childStoryQuestion.status === "paused";
  const isChildArchivedStatus = childStoryQuestion.status === "archived";
  const children = (
    <div className="ChildPrompts__QuickOptionWrapper" key={childStoryQuestion.id}>
      <div key={childStoryQuestion.id} className={classnames("ChildPrompts__Wrapper", `${childStoryQuestion.allowedTypes}`, `${noOptions ? "no_options" : ""}`)}>
        <PlainButton disabled={isChildPausedStatus || isChildArchivedStatus} className={classnames("ChildPrompts__Container", `${childStoryQuestion.allowedTypes}`)} onClick={() => handleSelectedPrompt(childStoryQuestion, index)}>
          <AnswerPromptContent childStoryQuestion={childStoryQuestion} />
        </PlainButton>
        {(isChildPausedStatus || isChildArchivedStatus) && <div className="ChildPrompts__DisabledOverlay" />}
      </div>
      {!noOptions && (
        <OldAndChildStoryQuestionQuickOptions
          storyQuestion={childStoryQuestion}
          onCreate={onCreate}
          onUpdate={onUpdate}
          onReplace={onReplace}
          onDelete={onDelete}
          onAccept={onAccept}
        />
      )}
    </div>
  );

  if ((isChildPausedStatus && !isParentTrendPausedStatus)
    || (isChildArchivedStatus && !isParentTrendArchivedStatus)) {
    return (
      <div className="AnswerPromptsDisabledTooltip">
        <Tooltip bottom tip={t("Collecting stories for this trend is closed")}>
          {children}
        </Tooltip>
      </div>
    );
  }

  return children;
};

const AnswerPrompts = ({
  isParentTrendPausedStatus,
  isParentTrendArchivedStatus,
  childStoryQuestions,
  currentOrganization,
  parentId,
  profile,
  onPosted,
  onDeleteStory,
  onUpdateStory,
  onCreate,
  onUpdate,
  onReplace,
  onDelete,
  onAccept,
  onNotification,
  noOptions
}) => {
  const location = useLocation();
  const [selectedPrompt, setSelectedPrompt] = useState();
  const [currentIndex, setCurrentIndex] = useState(1);

  useEffect(
    () => {
      if (location.hash === "#create" && location.pathname.startsWith("/stories/trend")) {
        const paths = location.pathname.split("/");
        if (`${parentId}` === paths[3]) {
          const childIndex = childStoryQuestions.findIndex(children => `${children.id}` === paths[5]);
          setCurrentIndex(childIndex + 1);
          setSelectedPrompt(childStoryQuestions[childIndex]);
        }
      }
    }, [location, childStoryQuestions, parentId]
  );

  // Doing the +1 because we need to sync with the pagination
  const getNextPendingPromptIndex = () => (
    childStoryQuestions.findIndex(
      prompt => (
        prompt.id !== selectedPrompt.id
          && prompt.peopleWhoAnswered.find(creator => creator.id === profile.id) === undefined
      )
    ) + 1
  );

  const handlePromptChange = index => {
    let newIndex = index;
    if (!index) {
      newIndex = getNextPendingPromptIndex();
    }
    if (newIndex === 0) {
      setSelectedPrompt();
      setCurrentIndex();
    } else {
      setCurrentIndex(newIndex);
      setSelectedPrompt(childStoryQuestions[newIndex - 1]);
    }
  };

  const handleSelectedPrompt = (childStoryQuestion, index) => {
    setSelectedPrompt(childStoryQuestion);
    setCurrentIndex(index + 1);
  };

  const handleClose = () => {
    setSelectedPrompt();
  };

  const children = (
    <div className="ChildPrompts">
      {selectedPrompt && (
        <PostStory
          organization={currentOrganization}
          initialOpen
          preFilledQuestion={selectedPrompt}
          openModal
          prompts={childStoryQuestions}
          currentIndex={currentIndex}
          onPosted={onPosted}
          onPromptChange={handlePromptChange}
          onCloseStoryModal={handleClose}
          onDeleteStory={onDeleteStory}
          onUpdateStory={onUpdateStory}
          onNotification={onNotification}
        />
      )}
      <h3 className="ChildPrompts-Title"><Trans>PROMPTS</Trans></h3>
      {childStoryQuestions.map((childStoryQuestion, index) => (
        <ChildStoryQuestion
          index={index}
          childStoryQuestion={childStoryQuestion}
          isParentTrendPausedStatus={isParentTrendPausedStatus}
          isParentTrendArchivedStatus={isParentTrendArchivedStatus}
          noOptions={noOptions}
          onCreate={onCreate}
          onUpdate={onUpdate}
          onReplace={onReplace}
          onDelete={onDelete}
          onAccept={onAccept}
          handleSelectedPrompt={handleSelectedPrompt}
        />
      ))}
      {(isParentTrendPausedStatus || isParentTrendArchivedStatus) && <div className="ChildPrompts__DisabledOverlay" />}
    </div>
  );

  if (isParentTrendPausedStatus || isParentTrendArchivedStatus) {
    return (
      <div className="AnswerPromptsDisabledTooltip">
        <Tooltip bottom tip={t("Collecting stories for this trend is closed")}>
          {children}
        </Tooltip>
      </div>
    );
  }

  return children;
};

export default AnswerPrompts;
